/**
 * Bottle detail page component
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, LoadingSpinner, Modal, ConfirmDialog, ZoomableImage } from '../components/common';
import { BottleForm } from '../components/bottles';
import { HuntForm } from '../components/hunts';
import { useBottle, useCreateBottle, useUpdateBottle, useDeleteBottle } from '../hooks/useBottles';
import { useCreateHunt } from '../hooks/useHunts';
import { useToast } from '../context/ToastContext';
import { formatDate, formatCurrency, formatProof, formatAge } from '../utils/formatters';
import './BottleDetail.css';

/**
 * Detail view for a single bottle
 * @returns {React.ReactElement} Page element
 */
export default function BottleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data: bottle, isLoading, error } = useBottle(id);
  const createBottle = useCreateBottle();
  const updateBottle = useUpdateBottle();
  const deleteBottle = useDeleteBottle();

  const createHunt = useCreateHunt();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHuntModal, setShowHuntModal] = useState(false);
  const [bottleForHunt, setBottleForHunt] = useState(null);
  const [showHuntConfirm, setShowHuntConfirm] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const handleEditSubmit = async (formData) => {
    try {
      const wasAlreadyEmpty = bottle?.status === 'Empty';
      await updateBottle.mutateAsync({ id, updates: formData });
      addToast({ type: 'success', message: 'Bottle updated successfully' });
      setShowEditModal(false);
      if (formData.status === 'Empty' && !wasAlreadyEmpty) {
        setBottleForHunt({
          bottle_name: formData.bottle_name,
          type: formData.type || null,
          distillery_id: formData.distillery_id || null,
        });
        setShowHuntConfirm(true);
      }
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to update bottle' });
    }
  };

  const handleAddToHuntConfirm = () => {
    setShowHuntConfirm(false);
    setShowHuntModal(true);
  };

  const handleCloneSubmit = async (formData) => {
    try {
      const newBottle = await createBottle.mutateAsync(formData);
      addToast({ type: 'success', message: 'Bottle cloned successfully' });
      setShowCloneModal(false);
      if (newBottle?.id) {
        navigate(`/bottles/${newBottle.id}`);
      }
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to clone bottle' });
    }
  };

  const handleHuntSubmit = async (huntData) => {
    try {
      await createHunt.mutateAsync(huntData);
      addToast({ type: 'success', message: 'Added to hunt list' });
      setShowHuntModal(false);
      setBottleForHunt(null);
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to add to hunt list' });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteBottle.mutateAsync(id);
      addToast({ type: 'success', message: 'Bottle deleted successfully' });
      navigate('/bottles');
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to delete bottle' });
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (error || !bottle) {
    return (
      <div className="bottle-detail__error">
        <h2>Bottle Not Found</h2>
        <p>The bottle you&apos;re looking for doesn&apos;t exist.</p>
        <Button onClick={() => navigate('/bottles')}>
          Back to Bottles
        </Button>
      </div>
    );
  }

  const characteristics = [
    bottle.single_barrel && 'Single Barrel',
    bottle.bottled_in_bond && 'Bottled-in-Bond',
    bottle.cask_strength && 'Cask Strength',
    bottle.full_proof && 'Full Proof',
    bottle.small_batch && 'Small Batch',
    bottle.double_barreled && 'Double Barreled'
  ].filter(Boolean);

  return (
    <div className="bottle-detail">
      <div className="bottle-detail__header">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          &larr; Back to Bottles
        </Button>
        <div className="bottle-detail__actions">
          <Button variant="secondary" onClick={() => setShowEditModal(true)}>
            Edit
          </Button>
          <Button variant="secondary" onClick={() => setShowCloneModal(true)}>
            Clone
          </Button>
          <Button variant="secondary" onClick={() => setShowHuntModal(true)}>
            + Hunt List
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
            Delete
          </Button>
        </div>
      </div>

      <div className="bottle-detail__content">
        <div className="bottle-detail__title-row">
          <h1 className="bottle-detail__name">{bottle.bottle_name}</h1>
          <div className="bottle-detail__badges">
            <span className={`bottle-detail__status bottle-detail__status--${bottle.status.toLowerCase()}`}>
              {bottle.status}
            </span>
          </div>
        </div>

        <div className="bottle-detail__type-row">
          <span className="bottle-detail__type">{bottle.type}</span>
          {bottle.size && <span className="bottle-detail__size">{bottle.size}</span>}
        </div>

        {bottle.distilleries && (
          <div className="bottle-detail__distillery">
            <span>{bottle.distilleries.distillery_name}</span>
            <span className="bottle-detail__country">{bottle.distilleries.country}</span>
          </div>
        )}

        <div className="bottle-detail__info">
          <div className="bottle-detail__grid">
            <div className="bottle-detail__field">
              <label>Proof</label>
              <p>{formatProof(bottle.proof)}</p>
            </div>

            <div className="bottle-detail__field">
              <label>Age</label>
              <p>{formatAge(bottle.age_months)}</p>
            </div>

            <div className="bottle-detail__field">
              <label>Purchase Price</label>
              <p>{formatCurrency(bottle.purchase_price)}</p>
            </div>

            <div className="bottle-detail__field">
              <label>Purchase Date</label>
              <p>{formatDate(bottle.purchase_date)}</p>
            </div>

            {bottle.status === 'Empty' && bottle.date_emptied && (
              <div className="bottle-detail__field">
                <label>Date Emptied</label>
                <p>{formatDate(bottle.date_emptied)}</p>
              </div>
            )}

            {bottle.store_pick && (
              <div className="bottle-detail__field">
                <label>Store Pick</label>
                <p>{bottle.store_pick}</p>
              </div>
            )}

            {bottle.finish_cask && (
              <div className="bottle-detail__field">
                <label>Finish Cask</label>
                <p>{bottle.finish_cask}</p>
              </div>
            )}
          </div>

          {characteristics.length > 0 && (
            <div className="bottle-detail__characteristics">
              <label>Characteristics</label>
              <div className="bottle-detail__tags">
                {characteristics.map(c => (
                  <span key={c} className="bottle-detail__tag">{c}</span>
                ))}
              </div>
            </div>
          )}

          {bottle.notes && (
            <div className="bottle-detail__field bottle-detail__field--full">
              <label>Notes</label>
              <p>{bottle.notes}</p>
            </div>
          )}

          {bottle.tasting_notes && (
            <div className="bottle-detail__field bottle-detail__field--full">
              <label>Tasting Notes</label>
              <p>{bottle.tasting_notes}</p>
            </div>
          )}

          {(bottle.image_front_url || bottle.image_back_url) && (
            <div className="bottle-detail__images">
              <label>Images</label>
              <div className="bottle-detail__images-grid">
                {bottle.image_front_url && (
                  <div className="bottle-detail__image-container">
                    <span className="bottle-detail__image-label">Front Label</span>
                    <img
                      src={bottle.image_front_url}
                      alt={`${bottle.bottle_name} front label`}
                      className="bottle-detail__image bottle-detail__image--clickable"
                      onClick={() => setLightboxImage({ url: bottle.image_front_url, label: 'Front Label' })}
                    />
                  </div>
                )}
                {bottle.image_back_url && (
                  <div className="bottle-detail__image-container">
                    <span className="bottle-detail__image-label">Back Label</span>
                    <img
                      src={bottle.image_back_url}
                      alt={`${bottle.bottle_name} back label`}
                      className="bottle-detail__image bottle-detail__image--clickable"
                      onClick={() => setLightboxImage({ url: bottle.image_back_url, label: 'Back Label' })}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bottle-detail__meta">
          <p>Created: {formatDate(bottle.created_at)}</p>
          <p>Updated: {formatDate(bottle.updated_at)}</p>
        </div>
      </div>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Bottle"
        size="lg"
      >
        <BottleForm
          bottle={bottle}
          onSubmit={handleEditSubmit}
          onCancel={() => setShowEditModal(false)}
          loading={updateBottle.isPending}
        />
      </Modal>

      <Modal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        title="Clone Bottle"
        size="lg"
      >
        <BottleForm
          bottle={bottle}
          isClone
          shareImages
          onSubmit={handleCloneSubmit}
          onCancel={() => setShowCloneModal(false)}
          loading={createBottle.isPending}
        />
      </Modal>

      <Modal
        isOpen={showHuntModal}
        onClose={() => { setShowHuntModal(false); setBottleForHunt(null); }}
        title="Add to Hunt List"
        size="md"
      >
        <HuntForm
          hunt={{
            bottle_name: bottleForHunt?.bottle_name ?? bottle.bottle_name,
            type: bottleForHunt?.type ?? bottle.type,
            distillery_id: (bottleForHunt?.distillery_id ?? bottle.distillery_id) || '',
            priority: '5',
          }}
          onSubmit={handleHuntSubmit}
          onCancel={() => { setShowHuntModal(false); setBottleForHunt(null); }}
          loading={createHunt.isPending}
        />
      </Modal>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Bottle"
        message={`Are you sure you want to delete "${bottle.bottle_name}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteBottle.isPending}
      />

      <ConfirmDialog
        isOpen={showHuntConfirm}
        onClose={() => { setShowHuntConfirm(false); setBottleForHunt(null); }}
        onConfirm={handleAddToHuntConfirm}
        title="Add to Hunt List?"
        message={`"${bottleForHunt?.bottle_name}" is now empty. Would you like to add it to your hunt list so you can find another?`}
        confirmText="Yes, Add to Hunt List"
        cancelText="No Thanks"
        variant="primary"
      />

      <Modal
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
        title={lightboxImage?.label}
        size="xl"
        className="bottle-detail__lightbox"
      >
        {lightboxImage && (
          <ZoomableImage
            key={lightboxImage.url}
            src={lightboxImage.url}
            alt={`${bottle.bottle_name} ${lightboxImage.label}`}
          />
        )}
      </Modal>
    </div>
  );
}
